/**
 * Ratifying a Priority List — the one client path (migration 987).
 *
 * `ratify_priority_list` is the act: proposed or decided → ratified, with its
 * PRIORITY_SET_RATIFIED governance event, in one transaction. It checks today's
 * rule (a member of the cycle's workspace) and then the cycle's "Items /
 * selection" decision right when one is set, and refuses with the database's
 * own sentence. A direct client write can no longer make a list ratified.
 *
 * `fetchRatifyRight` asks `can_ratify_priority_list` — the same answer — so a
 * page can leave the Ratify control out for a person who may not use it.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { actRightFromError, readActRight, type ActRight } from './actRight';

const RATIFY_FALLBACK = 'You may not ratify this Priority List.';

export async function fetchRatifyRight(prioritySetId: string): Promise<ActRight> {
  try {
    const { data, error } = await getTypedSupabaseClient().rpc('can_ratify_priority_list', {
      p_priority_set_id: prioritySetId,
    });
    if (error) return actRightFromError(error, 'ratify this Priority List');
    return readActRight(data, RATIFY_FALLBACK);
  } catch (err) {
    return actRightFromError({ message: err instanceof Error ? err.message : String(err) }, 'ratify this Priority List');
  }
}

export interface RatifiedPriorityList {
  priorityListId: string;
  fromState: string;
  ratifiedAt: string;
  ratifiedBy: string;
  governanceEventId: string;
}

export type RatifyResult = { ok: true; data: RatifiedPriorityList } | { ok: false; error: string };

export async function ratifyPriorityList(
  prioritySetId: string,
  opts?: { note?: string | null; publishDate?: string | null },
): Promise<RatifyResult> {
  try {
    const { data, error } = await getTypedSupabaseClient().rpc('ratify_priority_list', {
      p_priority_set_id: prioritySetId,
      p_note: opts?.note?.trim() || undefined,
      p_publish_date: opts?.publishDate || undefined,
    });
    if (error) return { ok: false, error: error.message || 'The Priority List was not ratified.' };
    const o = (data ?? {}) as Record<string, unknown>;
    return {
      ok: true,
      data: {
        priorityListId: String(o.priority_set_id ?? prioritySetId),
        fromState: String(o.from_state ?? ''),
        ratifiedAt: String(o.ratified_at ?? new Date().toISOString()),
        ratifiedBy: String(o.ratified_by ?? ''),
        governanceEventId: String(o.governance_event_id ?? ''),
      },
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}
